package com.trevorwiebe.timesheet.loads.presentation.admin

import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.loads.domain.model.Load
import kotlinx.datetime.LocalDate

sealed interface LoadsEvent {
    data object OnRetryLoadUser : LoadsEvent

    // Board filters (Customer + Driver). Applied client-side via LoadsState.filteredLoads.
    data class OnFilterByCustomer(val customerId: String?) : LoadsEvent

    /** Narrows the Dispatch board to a single employee (null = all). Only affects the Dispatch tab. */
    data class OnFilterByEmployee(val employeeId: String?) : LoadsEvent
    /** Resets every filter, including the Completed screen's date range, back to its default. */
    data object OnClearFilters : LoadsEvent

    // Completed Loads screen: pickup/drop location filters (null = all).
    data class OnFilterByPickupLocation(val locationId: String?) : LoadsEvent
    data class OnFilterByDropLocation(val locationId: String?) : LoadsEvent
    data class OnFilterByCarrier(val carrierId: String?) : LoadsEvent

    // Completed Loads screen: Start/End date filters. The *Click events open a picker, the
    // *Selected events commit a date, and the Dismiss* events close a picker without changing it.
    data object OnStartDateClick : LoadsEvent
    data object OnEndDateClick : LoadsEvent
    data class OnStartDateSelected(val date: LocalDate) : LoadsEvent
    data class OnEndDateSelected(val date: LocalDate) : LoadsEvent
    data object OnDismissStartPicker : LoadsEvent
    data object OnDismissEndPicker : LoadsEvent

    data object OnShowAddDialog : LoadsEvent
    data object OnDismissAddDialog : LoadsEvent
    data class OnUpdateForm(val form: AddLoadFormState) : LoadsEvent
    data object OnAddPickup : LoadsEvent
    data object OnAddDrop : LoadsEvent
    data class OnRemovePickup(val index: Int) : LoadsEvent
    data class OnRemoveDrop(val index: Int) : LoadsEvent
    data object OnSaveLoad : LoadsEvent

    // Load documents: a picked file uploads immediately; removing only drops it from the form.
    data class OnAttachmentSelected(val fileName: String, val bytes: ByteArray) : LoadsEvent
    data class OnRemoveAttachment(val index: Int) : LoadsEvent
    data class OnScanDocument(val pages: List<ByteArray>) : LoadsEvent
    data class OnOpenDocument(val documentUrl: String) : LoadsEvent

    /** Attaches a snapshot of [loadRate]'s pricing to the form (null clears the rate). */
    data class OnSelectLoadRate(val loadRate: LoadRate?) : LoadsEvent

    /** Opens the form pre-filled to edit [load]. */
    data class OnEditLoad(val load: Load) : LoadsEvent

    /** Sends a completed [load] back to the Unscheduled tab and closes the edit dialog. */
    data class OnUncompleteLoad(val load: Load) : LoadsEvent

    data class OnMoveLoad(
        val load: Load,
        val scheduledDate: LocalDate?,
        val employeeId: String,
        val carrierId: String = "",
    ) : LoadsEvent

    /**
     * Flips [load]'s dispatch confirmation: confirming publishes it to the assigned employee,
     * un-confirming unpublishes it but keeps the assignment.
     */
    data class OnToggleDispatchConfirmed(val load: Load) : LoadsEvent

    data object OnRequestDelete : LoadsEvent
    data object OnConfirmDelete : LoadsEvent
    data object OnDismissDelete : LoadsEvent

    // Duplicate-load dialog: copies the load being edited with new dates and load/PO/order numbers.
    data object OnShowDuplicateDialog : LoadsEvent
    data object OnDismissDuplicateDialog : LoadsEvent
    data class OnUpdateDuplicateForm(val form: DuplicateLoadFormState) : LoadsEvent
    data object OnSaveDuplicate : LoadsEvent
}
